import { Action, ActionType } from 'typesafe-actions';
import { iif, Observable, of, from, OperatorFunction, throwError } from 'rxjs';
import { ofType } from 'redux-observable';
import { catchError, concatMap, delay, map, mergeMap, retryWhen, switchMap, withLatestFrom } from 'rxjs/operators';
import { toReduxError } from '@universal/errors';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { fromApiRequest } from '@shared/apiClient/fromApiRequest';
import { PostPaymentOrderResponse, ProfileForm } from '@dtp/membership-service-types';
import { UpdateProfileSuccess } from '@appTypes/profileType';
import { GetHouseholdMemberResponse } from '@appTypes/householdMembers';
import { Actions, actions } from '@redux/modules/householdMembers';
import { TypedEpic } from './types';

// Epics
export const getHouseholdMembersEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_HOUSEHOLD_MEMBERS),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getHouseholdMembers.request>[]
    >,
    switchMap(() =>
      fromApiRequest((signal) =>
        apiClient.get<GetHouseholdMemberResponse>(ApiType.apimMembershipApi, {
          signal,
          url: ApiPaths[ApiType.apimMembershipApi].householdMembers,
        }),
      ).pipe(
        map((response) => actions.getHouseholdMembers.success(response.data)),
        catchError((err) =>
          of(
            actions.getHouseholdMembers.failure(
              toReduxError(
                err,
                'Oops, vi har problemer med motoren... Kunne ikke hente husstandsmedlemmene! Ta kontakt med kundesenteret hvis problemet fortsetter!',
              ),
            ),
          ),
        ),
      ),
    ),
  );

export const getHouseholdMemberDetailsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_HOUSEHOLD_MEMBER_DETAILS),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getHouseholdMemberDetails.request>[]
    >,
    switchMap(([action]) =>
      fromApiRequest((signal) =>
        apiClient.get<ProfileForm>(ApiType.apimMembershipApi, {
          signal,
          url: ApiPaths[ApiType.apimMembershipApi].householdMembersProfile(action.payload),
        }),
      ).pipe(
        map((response) => actions.getHouseholdMemberDetails.success(response.data, action.payload)),
        catchError((err) =>
          of(
            actions.getHouseholdMemberDetails.failure(
              toReduxError(
                err,
                'Oops, vi har problemer med motoren... Kunne ikke hente husstandsmedlemmet! Ta kontakt med kundesenteret hvis problemet fortsetter!',
              ),
              action.payload,
            ),
          ),
        ),
      ),
    ),
  );

const createHouseholdMemberOrderEpic: TypedEpic = (action$: Observable<Action<any>>, state$) => {
  const errorArray = [400, 422];

  return action$.pipe(
    ofType(Actions.CREATE_HOUSEHOLD_MEMBER_ORDER),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.createOrder.request>[]
    >,
    mergeMap(([action]) =>
      from(
        apiClient.post<PostPaymentOrderResponse>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].v2.productsPayment,
          data: action.payload,
          withCredentials: true,
        }),
      ).pipe(
        map((response) => actions.createOrder.success(response.data)),
        retryWhen((errors) =>
          errors.pipe(
            mergeMap((error) => {
              if (!errorArray.includes(error.status)) {
                return of(error);
              }
              return throwError(error);
            }),
            // Use concat map to keep the errors in order and make sure they
            // aren't executed in parallel
            concatMap((e, i) =>
              // Executes a conditional Observable depending on the result
              // of the first argument
              iif(
                () => i > 10,
                // If the condition is true we throw the error (the last error)
                throwError(e),
                // Otherwise we pipe this back into our stream and delay the retry
                of(e).pipe(delay(500)),
              ),
            ),
          ),
        ),
        catchError((error) => of(actions.createOrder.failure(error.response?.data || error))),
      ),
    ),
  );
};

export const updateHouseholdMemberEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.UPDATE_HOUSEHOLD_MEMBER),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.updateHouseholdMember.request>[]
    >,
    switchMap(([action]) =>
      from(
        apiClient.post<UpdateProfileSuccess>(ApiType.apimMembershipApi, {
          url: ApiPaths[ApiType.apimMembershipApi].householdMembersProfile(action.meta.memberId),
          data: action.payload,
        }),
      ).pipe(
        map((response) => actions.updateHouseholdMember.success(response.data, { memberId: action.meta.memberId })),
        catchError((error) =>
          of(actions.updateHouseholdMember.failure(error.response?.data || error, { memberId: action.meta.memberId })),
        ),
      ),
    ),
  );

export const epics: TypedEpic[] = [
  getHouseholdMembersEpic,
  getHouseholdMemberDetailsEpic,
  createHouseholdMemberOrderEpic,
  updateHouseholdMemberEpic,
];
